/**
 * VDI Telemetry — shared types and constants.
 *
 * This file is the single source of truth for:
 *  - The postMessage contract the VDI sends to the PWA.
 *  - The IndexedDB record shape persisted locally.
 *  - The batch API request/response shape.
 *  - All tuning constants (retry policy, capacity limits, field sizes).
 *
 * Nothing in this file has side effects. Keep it free of Angular DI and
 * browser-API calls so it can be imported in unit tests without setup.
 */

// ─── VDI → PWA postMessage contract ─────────────────────────────────────────

/**
 * Message the VDI sends via `window.postMessage` to report an activity event.
 * Agree this contract with the VDI team before enabling capture in production.
 */
export interface VdiTelemetryMessage {
  type: 'vdi-telemetry';
  /** Must exactly match the currently active PWA VDI session. */
  vdiSessionId: string;
  /** Must be one of {@link ALLOWED_VDI_EVENT_TYPES}. */
  eventType: string;
  /** Unix epoch milliseconds — VDI clock. */
  timestamp: number;
  /** Must increase monotonically within a VDI session. */
  sequence: number;
  /** JSON-safe, bounded to {@link MAX_EVENT_PAYLOAD_BYTES}. */
  payload: Record<string, unknown>;
}

/** Type-guard: narrows an unknown postMessage data value to {@link VdiTelemetryMessage}. */
export function isVdiTelemetryMessage(data: unknown): data is VdiTelemetryMessage {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    d['type'] === 'vdi-telemetry' &&
    typeof d['vdiSessionId'] === 'string' &&
    typeof d['eventType'] === 'string' &&
    typeof d['timestamp'] === 'number' &&
    typeof d['sequence'] === 'number' &&
    typeof d['payload'] === 'object' &&
    d['payload'] !== null &&
    !Array.isArray(d['payload'])
  );
}

// ─── HyLite VDI Event Type Constants ─────────────────────────────────────────

export const VDI_EVENT_CONSTANTS = {
  // Launch & Handshake
  USER_LAUNCH: 'user_launch',
  HYSECURE_VERINFO: 'hysecure_verinfo',
  HYSECURE_REALM_HELLO: 'hysecure_realm_hello',
  HYSECURE_REALM_FETCH: 'hysecure_realm_fetch',
  HYSECURE_LOGIN_HELLO: 'hysecure_login_hello',
  HYSECURE_ACCESS_POINTS: 'hysecure_access_points',
  HYSECURE_TURBO_CONFIG: 'hysecure_turbo_config',
  HYWORKS_LOGIN: 'hyworks_login',
  HYWORKS_GET_DESKTOPS: 'hyworks_get_desktops',
  HYWORKS_CONNECT_DESKTOP: 'hyworks_connect_desktop',
  SESSION_DETAILS: 'session_details',

  // Session Lifecycle & Diagnostics
  RDP_CONNECT: 'rdp_connect',
  RDP_DISCONNECT: 'rdp_disconnect',
  RDP_FAILURE: 'rdp_failure',
  RECOVERY_FAIL: 'recovery_fail',
  SESSION_FREEZE: 'session_freeze',
  INVALID_SURFACE: 'invalid_surface',
} as const;

// ─── Recovery Phase & Disconnect Constants ────────────────────────────────────

export const VDI_RECOVERY_PHASE = {
  DETECTED: 'detected',
  WAITING: 'waiting',
  DISCONNECT: 'disconnect',
  DISCONNECTED: 'disconnected',
  RELAUNCH: 'relaunch',
  RECOVERY: 'recovery',
} as const;

export const VDI_DISCONNECT_REASON = {
  LOGOUT: 'logout',
  DISCONNECT: 'disconnect',
  IDLE: 'idled',
  UNKNOWN: 'unknown',
} as const;

// ─── Allowlisted event types ─────────────────────────────────────────────────

/**
 * The only `eventType` values the PWA will accept from the VDI.
 * Unknown types are silently dropped (not dead-lettered) to avoid polluting
 * dead-letter with legitimate new types that arrive before the allowlist is
 * updated.
 */
export const ALLOWED_VDI_EVENT_TYPES = new Set<string>([
  // VDI Launch & Handshake Phase
  VDI_EVENT_CONSTANTS.USER_LAUNCH,
  VDI_EVENT_CONSTANTS.HYSECURE_VERINFO,
  VDI_EVENT_CONSTANTS.HYSECURE_REALM_HELLO,
  VDI_EVENT_CONSTANTS.HYSECURE_REALM_FETCH,
  VDI_EVENT_CONSTANTS.HYSECURE_LOGIN_HELLO,
  VDI_EVENT_CONSTANTS.HYSECURE_ACCESS_POINTS,
  VDI_EVENT_CONSTANTS.HYSECURE_TURBO_CONFIG,
  VDI_EVENT_CONSTANTS.HYWORKS_LOGIN,
  VDI_EVENT_CONSTANTS.HYWORKS_GET_DESKTOPS,
  VDI_EVENT_CONSTANTS.HYWORKS_CONNECT_DESKTOP,
  VDI_EVENT_CONSTANTS.SESSION_DETAILS,

  // Remote Desktop Session Phase & Fault Diagnostics
  VDI_EVENT_CONSTANTS.RDP_CONNECT,
  VDI_EVENT_CONSTANTS.RDP_DISCONNECT,
  VDI_EVENT_CONSTANTS.RDP_FAILURE,
  VDI_EVENT_CONSTANTS.RECOVERY_FAIL,
  VDI_EVENT_CONSTANTS.SESSION_FREEZE,
  VDI_EVENT_CONSTANTS.INVALID_SURFACE,

  // Generic / Activity types
  'vdi_activity',
  'vdi_connect',
  'vdi_disconnect',
  'vdi_error',
  'vdi_input',
  'vdi_clipboard',
  'vdi_display_change',
]);

// ─── IndexedDB record shape ──────────────────────────────────────────────────

/**
 * One row in the `telemetry_events` IndexedDB object store.
 * `id` is the auto-increment primary key, also used as the API event `id`.
 */
export interface TelemetryRecord {
  /** Auto-increment key; assigned by IndexedDB on write. Undefined before insertion. */
  id?: number;
  vdiSessionId: string;
  eventType: string;
  /** JSON-serialised payload with `vdiSessionId` and `sequence` embedded. */
  payload: string;
  timestamp: number;
  sequence: number;
  isSynced: boolean;
  createdAt: number;
  attemptCount: number;
  /** Earliest ms epoch at which a drain attempt may pick this record. */
  nextAttemptAt: number;
  /** Tab-unique string identifying the drain that currently holds this record; null when free. */
  leaseOwner: string | null;
  /** Epoch ms at which the lease expires and another drain may steal it. */
  leaseUntil: number | null;
  /** Last error string for diagnostics; not shown in the UI. */
  lastError: string | null;
}

/** One row in the `telemetry_dead_letter` object store. */
export interface TelemetryDeadLetterRecord {
  id?: number;
  originalId: number | null;
  vdiSessionId: string;
  eventType: string;
  payload: string;
  timestamp: number;
  sequence: number;
  reason: string;
  createdAt: number;
}

/** Metadata stored in the `telemetry_meta` object store. */
export interface TelemetryMeta {
  key: string;
  value: unknown;
}

// ─── API request / response ───────────────────────────────────────────────────

/** One item in the batch POST body. */
export interface TelemetryApiEvent {
  id: number;
  eventType: string;
  timestamp: number;
  /** JSON string matching the outer API contract. */
  payload: string;
}

/** Body of `POST /api/v2/telemetry/events`. */
export type TelemetryApiRequest = TelemetryApiEvent[];

/** Successful response body. Only `data.successIds` are safe to mark synced. */
export interface TelemetryApiResponse {
  success: boolean;
  data?: {
    successIds: number[];
  };
  message?: string;
  errors?: string;
}

// ─── Environment Configuration ───────────────────────────────────────────────

/** Environment-level configuration for the telemetry subsystem. */
export interface TelemetryEnvironmentConfig {
  /** Master kill-switch to enable or disable telemetry without code changes. */
  enabled: boolean;
  /**
   * Controls whether periodic/debounced API syncing is allowed while the VDI session is active.
   * - `true` (default): Telemetry batches periodically sync while the VDI is running.
   * - `false` (quiet mode): Events are only stored locally during VDI session; sync starts
   *   after the VDI session ends, on app startup, or on network recovery.
   */
  syncWhileVdiActive?: boolean;
  /** Active-tab polling interval (ms) for the background drain. */
  syncIntervalMs: number;
  /** Debounce delay (ms) between a queue write and the first drain attempt. */
  syncDebounceMs: number;
  /** Maximum events to include in one drain batch. */
  batchMaxEvents: number;
  /** Maximum pending-queue event count. */
  maxQueueEvents: number;
}

// ─── Sync / retry constants ───────────────────────────────────────────────────

/** Default debounce delay (ms) between a queue write and the first drain attempt. */
export const SYNC_DEBOUNCE_MS = 1_000;

/** Default active-tab polling interval (ms) for the background drain trigger. */
export const SYNC_POLL_INTERVAL_MS = 60_000;


/** Maximum events to include in one drain batch. */
export const BATCH_MAX_EVENTS = 100;

/** Maximum serialised request size (bytes) for one drain batch. */
export const BATCH_MAX_BYTES = 256 * 1024; // 256 KiB

/** Web Locks lock name for cross-tab drain serialisation. */
export const DRAIN_LOCK_NAME = 'jiopc-telemetry-drain';

/** Duration (ms) an IndexedDB lease is held before it expires and another drain may steal it. */
export const LEASE_DURATION_MS = 30_000;

/** Exponential backoff steps (ms) before full jitter kicks in. */
export const RETRY_BACKOFF_STEPS_MS = [5_000, 10_000, 20_000, 40_000];

/** Maximum retry delay (ms) after the fixed steps are exhausted. */
export const RETRY_MAX_JITTER_MS = 15 * 60 * 1000; // 15 minutes

// ─── Capacity / retention constants ──────────────────────────────────────────

/** Maximum accepted payload bytes per event (64 KiB). */
export const MAX_EVENT_PAYLOAD_BYTES = 64 * 1024;

/** Maximum pending-queue event count. When reached, the newest event is rejected. */
export const MAX_QUEUE_EVENTS = 20_000;

/** Maximum total pending-queue bytes (50 MiB). */
export const MAX_QUEUE_BYTES = 50 * 1024 * 1024;

/** Retain synced records for this many milliseconds before cleanup deletes them (7 days). */
export const SYNCED_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

/** Retain dead-letter records for this many milliseconds (14 days). */
export const DEAD_LETTER_RETENTION_MS = 14 * 24 * 60 * 60 * 1000;

// ─── IndexedDB database / store names ────────────────────────────────────────

export const TELEMETRY_DB_NAME = 'jiopc-vdi-telemetry-v1';
export const TELEMETRY_DB_VERSION = 1;
export const TELEMETRY_EVENTS_STORE = 'telemetry_events';
export const TELEMETRY_DEAD_LETTER_STORE = 'telemetry_dead_letter';
export const TELEMETRY_META_STORE = 'telemetry_meta';

// ─── Credential-like field name blocklist ────────────────────────────────────

/**
 * Any payload key that matches one of these patterns is stripped before the
 * record is written to IndexedDB. Prevents tokens/credentials leaking into
 * the local queue even if the VDI sends them by mistake.
 */
export const CREDENTIAL_FIELD_PATTERNS: RegExp[] = [
  /token/i,
  /secret/i,
  /password/i,
  /credential/i,
  /auth/i,
  /jwt/i,
  /bearer/i,
  /private/i,
];

/** Remove credential-like keys from a payload object (recursive up to depth 5). */
export function sanitizePayload(payload: Record<string, unknown>, depth = 0): Record<string, unknown> {
  if (depth > 5 || !payload || typeof payload !== 'object') return payload;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    const isSensitive = CREDENTIAL_FIELD_PATTERNS.some((re) => re.test(key));
    if (!isSensitive) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result[key] = sanitizePayload(value as Record<string, unknown>, depth + 1);
      } else {
        result[key] = value;
      }
    }
  }
  return result;
}

/**
 * Compute the next retry delay (ms) using exponential backoff with full jitter.
 * @param attemptCount — zero-indexed number of attempts already made.
 */
export function computeNextRetryDelayMs(attemptCount: number): number {
  const stepMs = RETRY_BACKOFF_STEPS_MS[attemptCount] ?? RETRY_MAX_JITTER_MS;
  // Full jitter: random in [0, stepMs]
  return Math.floor(Math.random() * stepMs);
}

/**
 * Normalizes any raw timestamp to a reliable 10-digit Unix Epoch Seconds integer
 * as expected by the Analytics API and backend database schema.
 * Handles 13-digit epoch ms, 10-digit epoch seconds, ISO-8601 strings, and invalid anomalies.
 */
export function normalizeTelemetryTimestamp(raw: unknown): number {
  const now = Date.now();
  let ms: number = now;

  if (typeof raw === 'number' && !isNaN(raw) && raw > 0) {
    if (raw < 10_000_000_000) {
      // Already in 10-digit epoch seconds
      return Math.floor(raw);
    }
    // 13-digit epoch ms
    if (raw > 1_700_000_000_000 && raw < 2_500_000_000_000) {
      ms = raw;
    }
  } else if (typeof raw === 'string') {
    const num = Number(raw);
    if (!isNaN(num) && num > 0) {
      return normalizeTelemetryTimestamp(num);
    }
    const parsed = Date.parse(raw);
    if (!isNaN(parsed) && parsed > 0) {
      ms = parsed;
    }
  }

  // Convert to 10-digit Unix epoch seconds
  return Math.floor(ms / 1000);
}

/**
 * Serializes a payload to a JSON string, ensuring credential sanitization (CWE-522).
 */
export function transformPayloadForApi(rawPayload: unknown): string {
  if (typeof rawPayload === 'string') {
    try {
      const parsed = JSON.parse(rawPayload);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return JSON.stringify(sanitizePayload(parsed as Record<string, unknown>));
      }
    } catch {
      return rawPayload;
    }
    return rawPayload;
  }
  if (rawPayload && typeof rawPayload === 'object' && !Array.isArray(rawPayload)) {
    return JSON.stringify(sanitizePayload(rawPayload as Record<string, unknown>));
  }
  return '{}';
}
